import type { CityConfig } from "@/pages/city-template/types";

export const milwaukeeConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Milwaukee",
  state: "Wisconsin",
  stateAbbr: "WI",
  slug: "milwaukee",
  route: "/cities/milwaukee",
  serviceAreaDesc: "Fast Passport Center has a registered office in Milwaukee at 833 E Michigan St, Milwaukee, WI 53202. Emergency and expedited passport services as fast as 24 hours. A+ BBB rated, registered U.S. Department of State passport courier.",
  schemaOfficeHours: [
    { dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "08:00", closes: "18:00" },
    { dayOfWeek: "Saturday", opens: "09:00", closes: "14:00" },
  ],
  title: "Emergency Passport Milwaukee, WI — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Milwaukee, WI — as fast as 24 hours. Wisconsin has no local passport agency. A+ BBB rated, registered State Dept. courier serving Milwaukee, Wauwatosa, Brookfield, Waukesha, and southeastern Wisconsin. Chicago agency courier access without the drive.",
  h1: "Emergency & Expedited Passport Services in Milwaukee, WI",
  heroSubtitle: "MKE flight to Cancun departing next week? Summerfest trip to European music festivals on short notice? Harley-Davidson or Rockwell Automation corporate travel to Asia? We help Milwaukee residents — from East Side professionals to Wauwatosa families to Brookfield executives — get their passports fast. Wisconsin has no passport agency, but our registered couriers hand-deliver to the Chicago Passport Agency — just 90 miles south. As fast as 24 hours. A+ BBB rated. No office visit required. No Chicago drive needed.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Milwaukee%20Wisconsin%20skyline%20at%20dusk%20with%20the%20lakefront%20harbor%20and%20Lake%20Michigan%20in%20the%20foreground%2C%20city%20lights%20reflecting%20on%20calm%20water%2C%20modern%20architecture%20with%20historic%20brick%20buildings%2C%20warm%20amber%20and%20deep%20blue%20tones%2C%20professional%20cityscape%20photography%2C%20wide%20angle%20view%2C%20clean%20composition%20with%20no%20text%20or%20people&width=1800&height=700&seq=milwaukee-hero-2026&orientation=landscape",
  heroImageAlt: "Milwaukee Wisconsin skyline with Lake Michigan waterfront at dusk",
  geoPosition: "43.0389;-87.9065",
  geoPlacename: "Milwaukee, Wisconsin",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "230 S Dearborn St, Suite 380, Chicago, IL 60604",
    distance: "Approximately 90 miles from Milwaukee — about 1.5 hours via I-94",
  },
  nearbyCitiesZips: [
    { name: "Downtown Milwaukee", zips: "53202, 53203, 53204" },
    { name: "Bay View", zips: "53207" },
    { name: "East Side", zips: "53202, 53211" },
    { name: "Wauwatosa", zips: "53213, 53226" },
    { name: "West Allis", zips: "53214, 53219" },
    { name: "Greenfield", zips: "53220, 53221" },
    { name: "Oak Creek", zips: "53154" },
    { name: "Franklin", zips: "53132" },
    { name: "South Milwaukee", zips: "53172" },
    { name: "Cudahy", zips: "53110" },
    { name: "Brookfield", zips: "53005, 53045" },
    { name: "Waukesha", zips: "53186, 53188" },
    { name: "New Berlin", zips: "53146, 53151" },
    { name: "Menomonee Falls", zips: "53051" },
    { name: "Mequon", zips: "53092, 53097" },
    { name: "Shorewood", zips: "53211" },
  ],
  benefits: [
    { icon: "ri-flashlight-line", title: "24-Hour Emergency Processing", desc: "Reserved courier access to the Chicago Passport Agency — as fast as 24 hours for Milwaukee-area travelers. No 90-mile drive required." },
    { icon: "ri-flight-takeoff-line", title: "MKE Airport + Chicago Hub Access", desc: "Milwaukee Mitchell International for regional flights, plus Chicago O'Hare and Midway for global connections — we serve all three airports." },
    { icon: "ri-building-2-line", title: "Manufacturing & Corporate Travel", desc: "Harley-Davidson, Rockwell Automation, Johnson Controls, Northwestern Mutual — we handle the passport so Milwaukee professionals handle the business." },
    { icon: "ri-music-line", title: "Summerfest & Festival Travel", desc: "The World's Largest Music Festival attracts global audiences. Many attendees extend their trips internationally — we help with last-minute passport needs." },
  ],
  internalLinks: [
    { name: "Expedited passport in Madison, WI", route: "/cities/madison-wi" },
    { name: "Fast passport services in Green Bay, WI", route: "/cities/green-bay" },
    { name: "Quick passport services in Racine, WI", route: "/cities/racine" },
    { name: "Wisconsin passport services overview", route: "/states/wisconsin" },
  ],
  fpcOfficeAddress: "833 E Michigan St, Milwaukee, WI 53202",
  officePhone: "(877) 253-0084",
  officeHours: [
    { days: "Monday – Friday", hours: "9:00 AM – 6:00 PM CST" },
    { days: "Saturday", hours: "10:00 AM – 2:00 PM CST" },
    { days: "Sunday", hours: "Closed" },
  ],
  fpcOfficeNote: "Visit the Fast Passport Center office at 833 E Michigan St in downtown Milwaukee by appointment only. Sign up on our website first to schedule your visit — walk-ins are not accepted. We offer full-service expedited and emergency passport processing for Milwaukee and southeastern Wisconsin travelers.",
  fpcOfficeDistance: "Located in downtown Milwaukee — serving Wauwatosa, Brookfield, Waukesha, and all of southeastern Wisconsin",
  mapEmbedUrl: "https://www.google.com/maps?q=833+E+Michigan+St,+Milwaukee,+WI+53202&output=embed",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Milwaukee and southeastern Wisconsin. From downtown Milwaukee and the Historic Third Ward to Wauwatosa and Brookfield, from Bay View and the East Side to Waukesha and Oak Creek — we serve all of Milwaukee County, Waukesha County, and beyond. Our courier service eliminates the need to drive 90 miles to the Chicago Passport Agency and compete for scarce appointments.",
  nearbyCitiesIntro: "We serve Milwaukee and all southeastern Wisconsin communities including Wauwatosa, West Allis, Greenfield, Brookfield, Waukesha, Oak Creek, Franklin, South Milwaukee, Cudahy, New Berlin, Menomonee Falls, Mequon, and Shorewood. We also serve travelers departing from Milwaukee Mitchell International Airport (MKE) and Chicago O'Hare (ORD) who need emergency passports.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Milwaukee as simple as possible — with the efficiency and reliability that Wisconsin professionals, festival travelers, and Milwaukee families need. Because we are a registered U.S. Department of State courier (not a reseller like FedEx or Staples), we pass those savings directly to you with our best price guarantee. Most Milwaukee travelers start online and ship documents overnight — no office visit required for most services. And no Chicago drive.",
  mistakesIntro: "Milwaukee travelers encounter unique passport issues — from Wisconsin's no-agency disadvantage to Summerfest-season appointment shortages to cross-border Canada travel confusion. Here are the most common passport mistakes we see from southeastern Wisconsin.",
  testimonialsIntro: "Milwaukee travelers — from East Side professionals to Wauwatosa families to Brookfield executives — trust us when their international departure is days away.",
  faqIntro: "Have questions about expedited passports in Milwaukee, WI? Here are the most common ones we hear from Wisconsin travelers.",
  statePageRoute: "/states/wisconsin",
  statePageName: "Wisconsin",
  servicesIntro: "Whether you need a renewal before an MKE international flight, an emergency passport for a last-minute corporate trip to Tokyo, or a first-time passport for a Milwaukee family vacation — Fast Passport Center handles it with reserved courier submission availability. Wisconsin has no passport agency, but our Chicago agency courier access means you get the fastest possible processing without leaving Milwaukee. As a registered State Department courier (not a reseller), we guarantee the best price and typically cost 30–100% less than FedEx, Staples, and other third-party passport courier resellers.",
  dmvWebsite: "https://www.dot.wisconsin.gov/drivers",
  dmvLabel: "Wisconsin DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Local Area", href: "#service-area" },
    { label: "Travel", href: "#travel-timing" },
    { label: "Destinations", href: "#destinations" },
    { label: "Reviews", href: "#testimonials" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Compare", href: "#comparison" },
    { label: "Mistakes", href: "#mistakes" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Chicago Agency hand-delivery — no drive required", "Wisconsin's fastest option with no local agency", "Predictable turnaround", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Drive 90 miles to Chicago Passport Agency", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "MKE flight to Cancun in two days? International business trip to Tokyo this week? Our emergency courier service submits directly via reserved agency slots in Chicago — as fast as 24 hours for Milwaukee-area travelers. No drive to Chicago required.",
      bullets: ["As fast as 24 hours", "Chicago Passport Agency courier access", "No 90-mile drive needed"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already hold a U.S. passport? We help Milwaukee residents renew fast — no agency visit required for most renewals. Perfect for East Side professionals, Wauwatosa families, and Brookfield executives preparing for international travel.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time applicants require an in-person acceptance clerk visit. We guide Milwaukee residents through every step — from downtown to Waukesha to Mequon — with clear instructions for the nearest acceptance location.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Replacements require additional forms. We guide you through DS-64 and everything needed to avoid costly mistakes — so your MKE international connection or Summerfest extension trip is not derailed.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors require parental participation and an in-person acceptance step. We provide clear instructions and checklists so your Milwaukee family vacation or heritage trip abroad stays on schedule.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or a legal name change — we assist with corrected passports so your travel documents match your reservations. Common for Milwaukee newlyweds after destination weddings.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-book-2-line",
      title: "Second Passport",
      description: "Need a second passport for frequent international travel? Ideal for Harley-Davidson dealers traveling to international rallies, Rockwell Automation engineers, and Wisconsin executives who travel while their primary passport is in visa processing.",
      bullets: ["Frequent traveler solutions", "Backup travel document", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
    {
      icon: "ri-file-damage-line",
      title: "Damaged Passport",
      description: "Replacing a significantly damaged passport is faster than you think. Wisconsin winters and Lake Michigan humidity claim their share of damaged passports — we guide you through the replacement process.",
      bullets: ["Damaged passport replacement", "Document review included", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=damaged-passport",
    },
    {
      icon: "ri-id-card-line",
      title: "Passport Card",
      description: "The passport card is a wallet-size travel document for land and sea crossings to Canada, Mexico, the Caribbean, and Bermuda. Popular with Milwaukee residents driving to Toronto or taking Great Lakes cruises.",
      bullets: ["Wallet-size convenience", "Land and sea travel to nearby destinations", "Same expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-card",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Milwaukee WI", description: "Urgent passport processing for Milwaukee Wisconsin travelers with immediate departure needs from MKE or Chicago airports." },
    { name: "Expedited Passport Renewal Milwaukee WI", description: "Fast passport renewal for Milwaukee Wisconsin residents with a previously-issued passport." },
    { name: "First-Time Passport Milwaukee WI", description: "New passport application services for Milwaukee Wisconsin residents applying for their first passport." },
    { name: "Child Passport Milwaukee WI", description: "Minor passport services for Milwaukee Wisconsin families with children under 16." },
    { name: "Name Change Passport Milwaukee WI", description: "Passport name correction after marriage, divorce, or legal name changes for Milwaukee residents." },
    { name: "Second Passport Milwaukee WI", description: "Second passport services for Milwaukee corporate executives and frequent international travelers." },
  ],
  testimonials: [
    {
      name: "Jennifer W.",
      location: "Wauwatosa, WI",
      rating: 5,
      text: "My passport expired two weeks before our Europe trip. I found out Wisconsin has no passport agency and panicked. Fast Passport Center couriered to Chicago and got me my renewed passport in 4 days. Absolutely incredible.",
      service: "Expedited Renewal",
    },
    {
      name: "Marcus D.",
      location: "Bay View, Milwaukee",
      rating: 5,
      text: "Business trip to Mexico City in 48 hours — my passport was expired. I called Fast Passport Center at 8 AM and had a new passport by the next afternoon. They drove to Chicago so I didn't have to.",
      service: "Emergency Passport",
    },
    {
      name: "Sarah T.",
      location: "Brookfield, WI",
      rating: 5,
      text: "Getting my kids' passports was so much easier with their guidance. They explained exactly what we needed for both parents to appear. All three passports processed in a week.",
      service: "Child Passport",
    },
    {
      name: "Kevin L.",
      location: "West Allis, WI",
      rating: 5,
      text: "Lost my passport before a Caribbean cruise sailing from Chicago. Fast Passport Center handled the DS-64, coordinated with the Chicago agency, and got me a replacement in time. Saved the whole family vacation.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "mke-1",
      name: "Milwaukee Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "341 W St Paul Ave",
      city: "Milwaukee",
      state: "WI",
      zip: "53203",
      phone: "(414) 270-2308",
      hours: "Mon–Fri: 8:30 AM – 5:00 PM",
      distance: "Downtown Milwaukee",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary downtown acceptance facility. Appointment required for passport applications. Call ahead to confirm passport hours.",
      icon: "ri-mail-line",
    },
    {
      id: "mke-2",
      name: "Wauwatosa Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "7600 W State St",
      city: "Wauwatosa",
      state: "WI",
      zip: "53213",
      phone: "(414) 453-2290",
      hours: "Mon–Fri: 9:00 AM – 5:00 PM",
      distance: "8 miles west of downtown Milwaukee",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Wauwatosa and Brookfield residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "mke-3",
      name: "Milwaukee County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "901 N 9th St, Room 105",
      city: "Milwaukee",
      state: "WI",
      zip: "53233",
      phone: "(414) 278-4064",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "1 mile from downtown Milwaukee",
      website: "https://county.milwaukee.gov/CountyClerk",
      websiteLabel: "Milwaukee County Clerk",
      notes: "Accepts walk-in passport applications. Photo services not available on-site. Bring your own photos.",
      icon: "ri-government-line",
    },
    {
      id: "mke-4",
      name: "Oak Creek Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "8515 S Howell Ave",
      city: "Oak Creek",
      state: "WI",
      zip: "53154",
      phone: "(414) 762-5543",
      hours: "Mon–Fri: 9:00 AM – 5:00 PM",
      distance: "12 miles south of downtown Milwaukee",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Oak Creek, Cudahy, and South Milwaukee residents. Appointment recommended. Close to MKE Airport.",
      icon: "ri-mail-line",
    },
    {
      id: "mke-5",
      name: "Brookfield City Hall",
      type: "City Clerk Passport Acceptance",
      address: "2000 N Calhoun Rd",
      city: "Brookfield",
      state: "WI",
      zip: "53005",
      phone: "(262) 796-6600",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "13 miles west of downtown Milwaukee",
      website: "https://www.ci.brookfield.wi.us/",
      websiteLabel: "City of Brookfield",
      notes: "Acceptance facility for Brookfield and Waukesha County residents. Photo services may be available nearby.",
      icon: "ri-government-line",
    },
    {
      id: "mke-6",
      name: "Waukesha Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "225 W Broadway",
      city: "Waukesha",
      state: "WI",
      zip: "53186",
      phone: "(262) 547-2300",
      hours: "Mon–Fri: 9:00 AM – 5:00 PM",
      distance: "20 miles west of downtown Milwaukee",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Full passport acceptance services for Waukesha County residents. Appointment required.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — typically booked 2+ weeks out in Chicago, walk-ins not accepted", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Required — 90-mile drive to Chicago Passport Agency", fpc: "Most Milwaukee travelers ship documents overnight" },
    { feature: "Processing Speed", agency: "Life-or-death emergencies only — strict eligibility with proof of travel", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection and delay", fpc: "Professional review to reduce processing delays" },
    { feature: "Courier Access", agency: "Not available to the public — agency serves emergencies only", fpc: "Reserved daily submission availability as registered courier" },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable — no guarantee", fpc: "Predictable processing based on selected speed option" },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only with proof of departure date", fpc: "Most U.S. citizens with required documentation" },
    { feature: "Cost vs Resellers", agency: "Government fees only — but you handle all shipping and logistics", fpc: "Best price guarantee — 30-100% less than FedEx, Staples & resellers" },
    { feature: "Milwaukee-Specific Guidance", agency: "Generic — no Wisconsin, MKE airport, or manufacturing corporate expertise", fpc: "Full Milwaukee/Chicago courier/Wisconsin corporate knowledge built in" },
  ],
  comparisonExtraColumns: undefined,
  mistakes: [
    {
      icon: "ri-error-warning-line",
      title: "Assuming Wisconsin Has a Passport Agency",
      description: "Wisconsin does not have a regional passport agency. The nearest is in Chicago — 90 miles south. Many Milwaukee residents discover this too late and scramble. Our courier service makes the Chicago drive unnecessary.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Driving to Chicago Without an Appointment",
      description: "The Chicago Passport Agency requires appointments for all visits — walk-ins are turned away without exception. Our couriers have pre-scheduled submission slots, guaranteeing access that individuals cannot achieve on their own.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Photo Standards Violations",
      description: "Many Milwaukee pharmacies and photo centers produce photos that fail State Department standards. We catch this in review before it causes a rejection.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Missing Parental Consent for Minors",
      description: "Milwaukee families frequently overlook the DS-3053 consent form when one parent is unavailable. This is an automatic rejection — we guide you through it correctly.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Summerfest Season Appointment Crunch",
      description: "June and July passport appointments in Milwaukee fill weeks in advance — between Summerfest visitors extending trips abroad and summer vacation planning. Start passport processing before festival season, not during it.",
    },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order. Takes just a few minutes from your home, office, or a Brady Street coffee shop." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist, instructions, and document review guidance tailored to your travel situation." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using an overnight shipping option. Most Milwaukee clients ship from home or a nearby UPS Store — no office visit needed." },
    { number: "04", icon: "ri-building-2-line", title: "We Courier to Chicago Passport Agency", description: "We hand-deliver your documents through our reserved access at the Chicago Passport Agency — so you do not have to make the 90-mile drive or compete for scarce public appointments." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport is completed within the timeframe you selected. No guessing, no hoping an appointment opens up — just certainty." },
  ],
  faqs: [
    { q: "Does Milwaukee or Wisconsin have a local passport agency?", a: "No. Wisconsin does not have a regional passport agency. The nearest regional passport agency for Milwaukee and all Wisconsin residents is the Chicago Passport Agency, located at 230 S Dearborn St, Suite 380, Chicago, IL 60604 — approximately 90 miles from Milwaukee. Fast Passport Center's registered couriers deliver directly to Chicago so you don't have to make the drive." },
    { q: "How fast can I get an emergency passport in Milwaukee?", a: "We offer Milwaukee-area clients expedited processing as fast as 24 hours through our registered courier service to the Chicago Passport Agency. Standard expedited service typically takes 5–10 business days. We review your documents before submission to catch errors that cause delays." },
    { q: "Can I walk into the Chicago Passport Agency from Milwaukee?", a: "No. The Chicago Passport Agency requires appointments for all visits — walk-ins are turned away without exception. Our couriers have pre-scheduled submission slots, which is why we can guarantee turnaround times that individuals cannot achieve on their own." },
    { q: "Do I need to visit a Milwaukee office?", a: "No. Our service is remote — you complete forms online, visit a local acceptance facility (a Milwaukee post office or county clerk), then ship documents overnight to our processing center. We courier to Chicago and return your passport to your Milwaukee address." },
    { q: "What is the best price for expedited passport service in Milwaukee?", a: "Government fees are $130 + $60 expedite fee. Our courier fee covers document review, overnight shipping, and State Department submission. Total cost is typically 30–100% lower than FedEx, Staples, and other third-party resellers. We offer a best price guarantee." },
    { q: "Where can I get a passport acceptance in Milwaukee?", a: "Milwaukee has acceptance facilities at the Main Post Office (341 W St Paul Ave), Milwaukee County Clerk (901 N 9th St), Wauwatosa Post Office, Oak Creek Post Office, Brookfield City Hall, and Waukesha Post Office. We maintain an updated list with hours, photo availability, and appointment options." },
    { q: "What airports do Milwaukee travelers typically use?", a: "Milwaukee Mitchell International Airport (MKE) is the primary airport, offering seasonal international flights. Many Milwaukee travelers also use Chicago O'Hare (ORD, 90 miles south) and Chicago Midway (MDW, 80 miles south) for more international route options. A valid passport is required for all international departures from any of these airports." },
    { q: "How do I check my Milwaukee passport status?", a: "Once submitted, we provide tracking at every stage. You can also check the State Department's online passport status system at travel.state.gov. Processing from Milwaukee to final return shipping typically matches your chosen speed tier." },
    { q: "Does Fast Passport Center serve Harley-Davidson and manufacturing employees?", a: "Yes. Fast Passport Center serves all of southeastern Wisconsin including major corporate employers. We understand manufacturing industry travel, international dealer conference schedules, and global supply chain travel needs. Our Chicago agency courier access is especially valuable for last-minute corporate travel." },
    { q: "Can you help with passports before Summerfest?", a: "Absolutely. Summerfest — the World's Largest Music Festival — draws visitors from across the globe. Many attendees combine their Milwaukee visit with international travel. Passport appointments fill weeks in advance during June and July. Our courier service operates independently of public appointment availability, so festival season timing is never an issue." },
  ],
  travelTiming: {
    title: "Travel & Passport Timing for Milwaukee Residents",
    intro: "Wisconsin has no passport agency — but Milwaukee's location just 90 miles from Chicago, combined with a strong manufacturing economy, world-famous festivals, and deep European heritage, creates distinct passport timing challenges. Here's what Milwaukee travelers need to know.",
    cards: [
      {
        icon: "ri-flight-takeoff-line",
        title: "MKE — Milwaukee Mitchell International Airport",
        subtitle: "Great Lakes regional hub with growing international service",
        description: "Milwaukee Mitchell International Airport (MKE) serves over 7 million passengers annually with seasonal international flights to Cancun, Punta Cana, and Toronto, plus connecting service worldwide. MKE's convenience — reachable from downtown in 15 minutes — makes it a favorite for Milwaukee travelers. However, many international routes require connections through Chicago O'Hare or Detroit. The airport's seasonal international schedule means summer and winter holiday periods see spikes in passport demand as Milwaukee families book warm-weather getaways.",
        tip: "MKE's seasonal international routes sell out quickly — travelers often book 3–6 months ahead, then discover passport expiration issues at online check-in. Always verify passport validity the moment you book, especially for destinations requiring 6 months validity beyond return.",
      },
      {
        icon: "ri-building-2-line",
        title: "Chicago Airport Access — O'Hare & Midway",
        subtitle: "Global hubs just 80–90 miles south of Milwaukee",
        description: "Milwaukee's proximity to Chicago gives residents access to two of America's busiest international airports. O'Hare (ORD) — 90 miles south via I-94 — is a United and American Airlines global hub with nonstop flights to 60+ international destinations. Midway (MDW) — 80 miles south — offers Southwest international connections. Many Milwaukee residents drive or take the Amtrak Hiawatha to Chicago for international flights. This proximity creates a unique dynamic: Milwaukee travelers often have more international route options than residents of similarly-sized cities, but no local passport agency to support those departures.",
        tip: "If you are driving from Milwaukee to Chicago for an international flight, your passport must be valid at your point of departure — not your destination. Chicago O'Hare check-in desks are strict on 6-month validity rules. Factor the 90-minute drive into your timeline and never arrive at O'Hare hoping to resolve a passport issue at the gate.",
      },
      {
        icon: "ri-music-line",
        title: "Summerfest, Festivals & International Tourism",
        subtitle: "The World's Largest Music Festival, German Fest, Irish Fest, Polish Fest",
        description: "Milwaukee's festival culture is legendary. Summerfest alone attracts 800,000+ visitors over 11 days, drawing international attendees and performers from across the globe. German Fest, Irish Fest, and Polish Fest celebrate Milwaukee's deep European heritage and bring international performers, vendors, and visitors. Many festival attendees extend their trips — flying to Europe after Summerfest or visiting ancestral hometowns in Germany, Ireland, and Poland. The June–August festival corridor creates a concentrated passport demand surge that overwhelms local acceptance facilities.",
        tip: "Festival season (June–August) is the worst time to start passport processing in Milwaukee. Post office appointments fill 3–4 weeks in advance. If you plan to extend your festival trip internationally, begin passport processing in March or April — before the summer rush.",
      },
      {
        icon: "ri-global-line",
        title: "German, Polish & European Heritage Travel",
        subtitle: "Milwaukee's deep European roots drive multi-generational travel",
        description: "Milwaukee has one of the richest European heritage profiles of any American city. German-Americans represent the largest ancestry group, with Milwaukee's German Fest being the largest German festival in North America. Polish-Americans are deeply rooted on the South Side, centered around St. Stanislaus and Polish Fest. Irish, Italian, and Scandinavian communities add to the mix. This heritage generates enormous multi-generational travel — grandparents, parents, and grandchildren traveling together to visit ancestral villages in Bavaria, Krakow, County Cork, and beyond. These trips often involve 4–8 passports coordinated in a single submission.",
        tip: "Multi-generational heritage trips require careful coordination. Different family members may be at different passport stages — some renewing, some applying for the first time. Fast Passport Center can process multiple family passports simultaneously, ensuring everyone is ready for the departure date. Start 8 weeks before travel for family groups.",
      },
    ],
    callout: {
      icon: "ri-map-pin-2-line",
      title: "Wisconsin's No-Agency Challenge — And Our Solution",
      description: "Wisconsin is one of the few states without a regional passport agency. For Milwaukee travelers with urgent international departures, the 90-mile drive to Chicago is inconvenient at best — and Chicago appointments are scarce anyway. Fast Passport Center's registered courier service eliminates this geographic disadvantage entirely, giving Milwaukee residents faster processing than they could achieve on their own, without ever leaving the city.",
    },
  },
  destinations: {
    title: "Where Milwaukee Travelers Go — And Why Passports Get Urgent",
    intro: "Milwaukee's European heritage, manufacturing economy, and Great Lakes location shape its international travel patterns. Here are the most common destinations and urgency patterns.",
    stats: [
      { stat: "35%", label: "of Milwaukee expedited requests are heritage-related — German, Polish, Irish, and Italian ancestry trips" },
      { stat: "2–4 Weeks", label: "average lead time Milwaukee travelers have from trip booking to international departure" },
      { stat: "Jun–Aug", label: "peak season when Summerfest, ethnic festivals, summer vacations, and Chicago hub departures collide" },
      { stat: "22 Hours", label: "shortest turnaround we have accommodated for a Milwaukee executive departing from O'Hare" },
    ],
    destinations: [
      { name: "Germany", context: "Strongest German-American heritage in the U.S., German Fest connections, Harley-Davidson dealer rallies, manufacturing supplier visits (Bosch, Siemens connections)", urgency: "Heritage trips and business travel booked 3–6 weeks out; manufacturing trade shows have fixed dates", icon: "ri-map-pin-2-line" },
      { name: "Poland & Eastern Europe", context: "South Side Polish-American community, Polish Fest connections, heritage trips to Krakow and Warsaw, growing business connections", urgency: "Heritage trips often involve multi-generational groups with 4–8 passport renewals needed simultaneously", icon: "ri-map-pin-2-line" },
      { name: "Ireland & United Kingdom", context: "Irish Fest connections, strong Irish-American community, Northwestern Mutual UK operations, Rockwell Automation European offices", urgency: "Corporate and heritage trips booked 2–4 weeks out; UK requires 6-month validity", icon: "ri-map-pin-2-line" },
      { name: "Mexico & Caribbean", context: "MKE seasonal nonstop to Cancun, Chicago O'Hare connections, winter getaways, destination weddings at all-inclusive resorts", urgency: "Winter bookings peak September through November for January–March travel", icon: "ri-map-pin-2-line" },
      { name: "Italy & Southern Europe", context: "Italian-American Festa Italiana connections, culinary tourism, Mediterranean cruises departing from Chicago or Florida ports", urgency: "Summer heritage and leisure travel booked 2–4 weeks before departure", icon: "ri-map-pin-2-line" },
      { name: "Japan & Asia", context: "Harley-Davidson Asia-Pacific operations, Rockwell Automation Asian markets, Johnson Controls global manufacturing visits", urgency: "Manufacturing travel booked 1–2 weeks out; Japan requires 6-month validity", icon: "ri-map-pin-2-line" },
    ],
    crossLinksTitle: "Explore Other Wisconsin Service Areas",
    crossLinksDesc: "We serve travelers across the Badger State with the same fast, reliable passport processing.",
    crossLinks: [
      { name: "Madison", route: "/cities/madison-wi" },
      { name: "Green Bay", route: "/cities/green-bay" },
      { name: "Racine", route: "/cities/racine" },
    ],
  },
  photoLocations: [
    { name: "CVS Pharmacy — N Oakland Ave", address: "2520 N Oakland Ave, Milwaukee, WI 53211", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/photo" },
    { name: "Walgreens — W Capitol Dr", address: "2701 W Capitol Dr, Milwaukee, WI 53206", hours: "Mon–Sun: 7AM–10PM", website: "https://www.walgreens.com/photo" },
    { name: "FedEx Office — N Water St", address: "770 N Water St, Milwaukee, WI 53202", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM", website: "https://www.office.fedex.com/default/office" },
    { name: "UPS Store — N Mayfair Rd", address: "11616 W Burleigh St, Wauwatosa, WI 53222", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM", website: "https://www.theupsstore.com/print/passport-photos" },
    { name: "Walmart — S 27th St", address: "6201 S 27th St, Franklin, WI 53132", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com/store/finder" },
    { name: "CVS — Brookfield Rd", address: "17145 W Bluemound Rd, Brookfield, WI 53005", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/photo" },
  ],
  dmvLocations: [
    {
      name: "WI DMV — Milwaukee South",
      address: "7131 W Howard Ave, Milwaukee, WI 53220",
      hours: "Mon–Fri: 8:30AM–4:30PM",
      note: "REAL ID and driver's license renewals. Appointment recommended.",
      featured: true,
    },
    {
      name: "WI DMV — Wauwatosa",
      address: "11711 W North Ave, Wauwatosa, WI 53226",
      hours: "Mon–Fri: 8:30AM–4:30PM",
      note: "Convenient for west side Milwaukee and Waukesha County residents.",
      featured: false,
    },
    {
      name: "WI DMV — Oak Creek",
      address: "9000 S Howell Ave, Oak Creek, WI 53154",
      hours: "Mon–Fri: 8:30AM–4:30PM",
      note: "South Milwaukee area DMV. Close to MKE Airport. Appointment required for REAL ID.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "MKE",
      name: "Milwaukee Mitchell International Airport",
      distance: "6 miles from downtown Milwaukee",
      address: "5300 S Howell Ave, Milwaukee, WI 53207",
      note: "Milwaukee's primary airport with seasonal international flights to Cancun, Punta Cana, and Toronto. Connecting service worldwide through major hubs.",
      flightUrl: "https://www.mitchellairport.com/",
      flightLabel: "MKE Flight Info",
      featured: true,
    },
    {
      code: "ORD",
      name: "Chicago O'Hare International Airport",
      distance: "90 miles south of Milwaukee via I-94",
      address: "10000 W O'Hare Ave, Chicago, IL 60666",
      note: "Major international hub used by many Milwaukee travelers for transatlantic and transpacific flights. United and American Airlines global hub with 60+ international destinations.",
      flightUrl: "https://www.flychicago.com/ohare/",
      flightLabel: "O'Hare Airport",
      featured: false,
    },
    {
      code: "MDW",
      name: "Chicago Midway International Airport",
      distance: "80 miles south of Milwaukee",
      address: "5700 S Cicero Ave, Chicago, IL 60638",
      note: "Alternative Chicago airport for Milwaukee travelers. Southwest hub with some international charter flights. Closer to Milwaukee than O'Hare.",
      flightUrl: "https://www.flychicago.com/midway/",
      flightLabel: "Midway Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      icon: "ri-user-smile-line",
      label: "Birth Certificate",
      desc: "Request certified birth certificates from the Wisconsin Department of Health Services Vital Records office. Online, mail, or in-person options available for Milwaukee County and all Wisconsin counties.",
      url: "https://www.dhs.wisconsin.gov/vitalrecords/birth.htm",
    },
    {
      icon: "ri-file-list-2-line",
      label: "Death Certificate",
      desc: "Obtain certified death certificates through Wisconsin Vital Records. Required for estate and legal matters in Milwaukee County.",
      url: "https://www.dhs.wisconsin.gov/vitalrecords/death.htm",
    },
    {
      icon: "ri-hearts-line",
      label: "Marriage Certificate",
      desc: "Request marriage license records from the Milwaukee County Register of Deeds at 901 N 9th St. Certified copies for name changes and passport applications.",
      url: "https://county.milwaukee.gov/RegisterofDeeds",
    },
    {
      icon: "ri-file-copy-line",
      label: "Divorce Decree",
      desc: "Obtain certified divorce records through the Milwaukee County Clerk of Circuit Court. Required for name change passport corrections.",
      url: "https://www.wicourts.gov/courts/circuit/milwaukee.htm",
    },
  ],
};